import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

/**
 * GDPR Art. 17 - Right to Erasure ("Right to be Forgotten")
 * Anonymizes user data and disables the account.
 * 
 * Note: Financial/fiscal data is retained per Spanish law:
 * - Ley General Tributaria Art. 66: 4 years for tax documents
 * - Código de Comercio Art. 30: 6 years for accounting records
 * These records are anonymized (personal identifiers removed) but kept.
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  const { confirmation } = req.body || {};

  if (confirmation !== 'DELETE_MY_ACCOUNT') {
    return res.status(400).json({
      error: 'Se requiere confirmación. Envía { "confirmation": "DELETE_MY_ACCOUNT" }',
    });
  }

  try {
    const supabaseAdmin = getSupabaseAdminClient();
    const userId = user.id;

    // 1. Log the erasure request BEFORE deleting
    await supabaseAdmin.from('gdpr_data_requests').insert({
      user_id: userId,
      request_type: 'erasure',
      status: 'processing',
      requested_at: new Date().toISOString(),
      response_notes: 'Solicitud de eliminación de cuenta iniciada por el usuario',
    });

    // 2. Anonymize profile data (keep record for audit trail)
    await supabaseAdmin
      .from('profiles')
      .update({
        nombre: 'Usuario Eliminado',
        email: `deleted_${userId.substring(0, 8)}@anonimizado.local`,
        telefono: null,
        notas_internas: null,
        estado: 'inactivo',
        codigo: null,
        tipo_empresa: null,
        provincia: null,
        nombre_fiscal: null,
        cif: null,
      })
      .eq('id', userId);

    // 3. Anonymize cliente data (if exists) - keep financial structure for legal compliance
    const { data: cliente } = await supabaseAdmin
      .from('clientes')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    if (cliente) {
      await supabaseAdmin
        .from('clientes')
        .update({
          nif: `ANONIMIZADO_${cliente.id.substring(0, 8)}`,
          razon_social: 'Cliente Eliminado',
          nombre_comercial: 'Cliente Eliminado',
          persona_contacto: 'Eliminado',
          telefono: '000000000',
          email: `deleted_${cliente.id.substring(0, 8)}@anonimizado.local`,
          direccion_fiscal: 'Anonimizado',
          estado: 'inactivo',
          codigo: null,
          tipo_empresa: null,
          provincia: null,
          nombre_fiscal: null,
          cif: null,
        })
        .eq('id', cliente.id);
    }

    // 4. Delete non-essential data (respecting CASCADE rules)
    // Notification history - operational, can be deleted
    await supabaseAdmin
      .from('historial_notificaciones')
      .delete()
      .eq('usuario_id', userId);

    // Notification preferences
    await supabaseAdmin
      .from('preferencias_notificaciones')
      .delete()
      .eq('usuario_id', userId);

    // User preferences
    await supabaseAdmin
      .from('user_preferences')
      .delete()
      .eq('user_id', userId);

    // Notification settings
    await supabaseAdmin
      .from('notification_settings')
      .delete()
      .eq('user_id', userId);

    // Assistant conversations and messages (CASCADE will handle messages)
    if (cliente) {
      await supabaseAdmin
        .from('conversaciones_asistente')
        .delete()
        .eq('cliente_id', cliente.id);
    }

    // 5. Revoke all active consents
    await supabaseAdmin
      .from('gdpr_consent_log')
      .insert({
        user_id: userId,
        consent_type: 'data_processing',
        granted: false,
        revoked_at: new Date().toISOString(),
        consent_text_version: 'account_deletion',
      });

    // 6. Log audit trail
    await supabaseAdmin.from('auditoria_financiera').insert({
      usuario_id: userId,
      usuario_nombre: 'Sistema GDPR',
      usuario_rol: 'system',
      accion: 'gdpr_erasure',
      entidad_tipo: 'profile',
      entidad_id: userId,
      descripcion: 'Cuenta eliminada por solicitud GDPR Art. 17. Datos personales anonimizados. Datos fiscales retenidos por obligación legal.',
      datos_anteriores: { email: user.email },
      datos_nuevos: { status: 'anonymized' },
    });

    // 7. Update the GDPR request status
    await supabaseAdmin
      .from('gdpr_data_requests')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        response_notes: 'Cuenta anonimizada. Datos fiscales retenidos por obligación legal (LGT Art.66, CC Art.30).',
      })
      .eq('user_id', userId)
      .eq('request_type', 'erasure')
      .eq('status', 'processing');

    // 8. Delete the Supabase auth user (this will sign them out everywhere)
    const { error: deleteAuthError } = await supabaseAdmin.auth.admin.deleteUser(userId);

    if (deleteAuthError) {
      console.error('[GDPR Delete] Error deleting auth user:', deleteAuthError);
      // Don't fail the request - data is already anonymized
    }

    return res.status(200).json({
      success: true,
      message: 'Cuenta eliminada correctamente. Tus datos personales han sido anonimizados. Los datos fiscales se mantendrán según la legislación española vigente.',
      retained_data: {
        reason: 'Obligación legal',
        laws: ['Ley General Tributaria Art. 66 (4 años)', 'Código de Comercio Art. 30 (6 años)'],
        data_types: ['Documentos fiscales (anonimizados)', 'Asientos contables (anonimizados)', 'Libro registro (anonimizado)'],
      },
    });
  } catch (error: any) {
    console.error('[GDPR Delete] Error:', error);
    return res.status(500).json({ error: 'Error al procesar la eliminación de cuenta.' });
  }
}

export default withAuth(handler);
